import { TFunction } from 'i18next';
import { z } from 'zod';

const isValidDate = (value: string) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;

  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return (
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day)
  );
};

const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+(?:\.[A-Za-z])?$/;
const PARTIAL_APP_VERSION_PATTERN = /^\d*(?:\.\d*){0,2}\.?$/;
const COMPLETE_APP_VERSION_WITH_SUFFIX_PATTERN = /^\d+\.\d+\.\d+\.[A-Z]$/;

export const maskAppVersionInput = (value: string): string | null => {
  const normalized = value.toUpperCase().replace(/[^0-9A-Z.]/g, '');
  return PARTIAL_APP_VERSION_PATTERN.test(normalized) ||
    COMPLETE_APP_VERSION_WITH_SUFFIX_PATTERN.test(normalized)
    ? normalized
    : null;
};

export const getAppStatusIncidentSchema = (t: TFunction) =>
  z.object({
    date: z.string().refine(isValidDate, t('appStatus.validation.invalidDate')),
    errorDescription: z.string().trim(),
    errorDescriptionImage: z.instanceof(File).nullable().optional(),
    possibleSolution: z.string().trim(),
    possibleSolutionImage: z.instanceof(File).nullable().optional(),
    public: z.boolean(),
    visibleApp: z.boolean(),
    severity: z.enum(['high', 'medium', 'low']),
    status: z.enum(['investigating', 'needsInfo', 'nextVersion', 'resolved']),
    summary: z.string().trim().min(1, t('common.validation.fieldRequired')).max(500),
    title: z.string().trim().min(1, t('common.validation.fieldRequired')).max(255),
    version: z.preprocess(
      (value) => value ?? '',
      z
        .string()
        .trim()
        .max(50)
        .refine(
          (value) => !value || APP_VERSION_PATTERN.test(value),
          t('appStatus.validation.invalidVersion'),
        )
        .transform((value) => (value ? value.toUpperCase() : null)),
    ),
  });
